import type { ReactNode } from "react";
import "../styles/visitor-foot.css";

import { NovaWordmark, VisitorMark, type VisitorLogo } from "../logos.js";

export function WheelLogo({
  logo,
  size,
}: {
  readonly logo: VisitorLogo;
  readonly size?: number | undefined;
}): ReactNode {
  return (
    <span className="logo" style={size === undefined ? undefined : { height: size }}>
      <VisitorMark logo={logo} />
    </span>
  );
}

export function WField({
  value,
  onValue,
  placeholder,
  type = "text",
  disabled = false,
  label,
  autoComplete,
}: {
  readonly value: string;
  readonly onValue: (next: string) => void;
  readonly placeholder: string;
  readonly type?: "text" | "email" | "tel";
  readonly disabled?: boolean;
  readonly label: string;
  readonly autoComplete?: string | undefined;
}): ReactNode {
  return (
    <span className="w-field">
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete={autoComplete}
        aria-label={label}
        onChange={(event) => onValue(event.target.value)}
      />
      <span className="sr">{label}</span>
    </span>
  );
}

export function WCode({
  value,
  onValue,
  label,
  invalid,
}: {
  readonly value: string;
  readonly onValue: (next: string) => void;
  readonly label: string;
  readonly invalid?: boolean | undefined;
}): ReactNode {
  return (
    <input
      className="field code"
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={6}
      value={value}
      placeholder="000000"
      aria-label={label}
      aria-invalid={invalid === true ? true : undefined}
      onChange={(event) => onValue(event.target.value.replace(/\D/g, "").slice(0, 6))}
    />
  );
}

export function Cta({
  ready,
  disabled = false,
  onClick,
  children,
}: {
  readonly ready: boolean;
  readonly disabled?: boolean;
  readonly onClick?: (() => void) | undefined;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <span className="cta-wrap" data-ready={ready && !disabled ? "true" : "false"}>
      <button type="button" disabled={disabled || !ready} onClick={onClick}>
        {children}
      </button>
    </span>
  );
}

export function ConsentRow({
  line,
  on,
  bad,
  onFlip,
}: {
  readonly line: { readonly id: string; readonly text: string; readonly required: boolean };
  readonly on: boolean;
  readonly bad: boolean;
  readonly onFlip: () => void;
}): ReactNode {
  return (
    <label className={`w-consent${bad ? " w-consent-bad" : ""}`}>
      <input type="checkbox" checked={on} onChange={onFlip} />
      <span>
        {line.text}
        {line.required ? <span className="req"> *</span> : null}
      </span>
    </label>
  );
}

/** One link in the footer: a page to open, or something to do (Cookie settings, Delete my data). */
export type FootLink = {
  readonly label: string;
  readonly href?: string | undefined;
  readonly onClick?: (() => void) | undefined;
};

/**
 * What a UK registration page has to carry, and nothing else (the October
 * hand-off's visitor-foot.jsx, which sets out why):
 *   Companies Act 2006 and the Trading Disclosures Regulations 2015: the
 *   registered name, number, place of registration and registered office;
 *   E-Commerce Regulations 2002, reg 6: a working email address and the VAT
 *   number; UK GDPR arts 13/14: the privacy notice, easily found, with the
 *   terms beside it because the visitor agrees to them on this page.
 * The details are shared/region.js's for Northern Makers Ltd. Each is its own
 * item so a narrow screen breaks between facts, never inside one.
 */
export const FOOT_REGISTERED = [
  "© 2026 Northern Makers Ltd",
  "Nova Exhibits is a trading name of Northern Makers Ltd",
  "Registered in England, no. 12178624",
  "Unit 5, Overtown Farm, Warburton WA13 9TG",
  "VAT GB340240747",
] as const;
export const FOOT_MAIL = "support@novaexhibits.co.uk";

/**
 * The links when an app supplies none: the documents on the main site, and
 * the data request. Cookie settings is an action rather than a page, so it is
 * drawn only where an app can reopen its consent choices and passes it in.
 */
export const DEFAULT_FOOT_LINKS: readonly FootLink[] = [
  { label: "Privacy notice", href: "https://novaexhibits.co.uk/privacy" },
  { label: "Terms", href: "https://novaexhibits.co.uk/terms" },
  { label: "Cookie policy", href: "https://novaexhibits.co.uk/cookies" },
  { label: "Delete my data", href: "https://novaexhibits.co.uk/data-request" },
];

/**
 * The one visitor footer: Registration, the Wheel and the Board. Styled by
 * styles/visitor-foot.css against the --sf-* tokens each app maps.
 *
 * The Client system never shows a Nova mark (the October brief), so a client
 * show's footer is the legal block and the links alone.
 */
export function SiteFoot({
  links = DEFAULT_FOOT_LINKS,
  system,
}: {
  readonly links?: readonly FootLink[] | undefined;
  readonly system?: string | undefined;
} = {}): ReactNode {
  return (
    <footer className="site-foot">
      <div className="foot-in">
        <div className="foot-top">
          {system === "client" ? null : (
            <span className="foot-brand nova-only">
              <NovaWordmark />
            </span>
          )}
          <div className="foot-clip foot-clip-links">
            <nav className="foot-links" aria-label="Legal">
              {links.map((link) =>
                link.onClick !== undefined ? (
                  <a
                    key={link.label}
                    href={link.href ?? "#"}
                    onClick={(event) => {
                      event.preventDefault();
                      link.onClick?.();
                    }}
                  >
                    {link.label}
                  </a>
                ) : (
                  <a key={link.label} href={link.href}>
                    {link.label}
                  </a>
                ),
              )}
            </nav>
          </div>
        </div>
        <div className="foot-base">
          <div className="foot-clip">
            <ul className="foot-reg">
              {FOOT_REGISTERED.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
          <a className="foot-mail" href={`mailto:${FOOT_MAIL}`}>
            {FOOT_MAIL}
          </a>
        </div>
      </div>
    </footer>
  );
}

export function FootFab({ open, onClick }: { readonly open: boolean; readonly onClick: () => void }): ReactNode {
  return (
    <button
      type="button"
      className="fab"
      aria-expanded={open}
      aria-label={open ? "Close the footer" : "Open the footer"}
      onClick={onClick}
    >
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M12 19V5M5 12l7-7 7 7"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}
